import { __decorate, __metadata, __param } from "tslib";
import { GoodTableColumnFixedType } from '@/components/GoodTable/GoodTable.types';
import { Symbols } from '@/symbols';
import { getDateFormatYMD } from '@/utils/date';
import { inject, injectable } from 'inversify';
import { toThousands } from '@/utils/number';
import { getBarOptions } from '@/service/CompanyBase/CompanyDataAnalysis/echartsOption';
import { GRANULARITY, AccountDataAnalysisEventKey, AccountDataAnalysisDataSet, AccountDataAnalysisKeyType, AccountDataAnalysisViewType, AccountDataAnalysisUserSummaryKey } from '@/apis/service/account/AccountDataAnalysisApi.type';
import _ from 'lodash';
import { BaseDataAnalysisChartService } from '@/service/common/BaseDataAnalysisChartService';
import { BaseDataAnalysisOptions } from '@/service/common/BaseDataAnalysisOptions';
import { E_ROUTE_ACCOUNT } from '@/router/routes/route.types';
const sortFunctionMap = {
    [AccountDataAnalysisKeyType.EUREKA_USER_LOGIN]: `${AccountDataAnalysisEventKey.EUREKA_LOGIN_CNT}:desc`,
    [AccountDataAnalysisKeyType.EUREKA_RD_USER_BEHAVIOR]: `${AccountDataAnalysisEventKey.EUREKA_RD_TECHNICAL_QA}:desc`,
    [AccountDataAnalysisKeyType.EUREKA_IP_USER_BEHAVIOR]: `${AccountDataAnalysisEventKey.EUREKA_IP_NOVELTY_SEARCH}:desc`,
    [AccountDataAnalysisKeyType.EUREKA_MATERIAL_USER_BEHAVIOR]: `${AccountDataAnalysisEventKey.EUREKA_MATERIALS_MATERIAL_PERFORMANCE_ANALYSIS}:desc`,
    [AccountDataAnalysisKeyType.EUREKA_USER_COMPREHENSIVE]: `${AccountDataAnalysisEventKey.EUREKA_LOGIN_CNT}:desc`,
};
export const eurekaRdSortList = [
    AccountDataAnalysisEventKey.EUREKA_RD_TECHNICAL_QA,
    AccountDataAnalysisEventKey.EUREKA_RD_FIND_SOLUTION,
    AccountDataAnalysisEventKey.EUREKA_RD_QUICK_RESEARCH,
    AccountDataAnalysisEventKey.EUREKA_RD_EVALUATE_FEASIBILITY,
    AccountDataAnalysisEventKey.EUREKA_RD_FEASIBILITY_ANALYSIS_ASSISTANT,
    AccountDataAnalysisEventKey.EUREKA_RD_HOMEPAGE_SEARCH,
    AccountDataAnalysisEventKey.EUREKA_RD_PATENT_SEARCH,
    AccountDataAnalysisEventKey.EUREKA_RD_LITERATURE_SEARCH,
    AccountDataAnalysisEventKey.EUREKA_RD_IDEA_VALIDATION,
    AccountDataAnalysisEventKey.EUREKA_RD_INVENTION_DISCLOSURE
];
export const eurekaIPSortList = [
    AccountDataAnalysisEventKey.EUREKA_IP_NOVELTY_SEARCH,
    AccountDataAnalysisEventKey.EUREKA_IP_PATENT_DRAFTING_CN,
    AccountDataAnalysisEventKey.EUREKA_IP_PATENT_DRAFTING_US,
    AccountDataAnalysisEventKey.EUREKA_IP_PATENT_DRAFTING_EU,
    AccountDataAnalysisEventKey.EUREKA_IP_INVENTION_DISCLOSURE,
    AccountDataAnalysisEventKey.EUREKA_IP_PATENT_TRANSLATION,
    AccountDataAnalysisEventKey.EUREKA_IP_DESIGN_FTO_SEARCH
];
export const eurekaMaterialsSortList = [
    AccountDataAnalysisEventKey.EUREKA_MATERIALS_MATERIAL_PERFORMANCE_ANALYSIS,
    AccountDataAnalysisEventKey.EUREKA_MATERIALS_ALLOY_COMPOSITION_ANALYSIS,
    AccountDataAnalysisEventKey.EUREKA_MATERIALS_MULTI_D_SEARCH,
    AccountDataAnalysisEventKey.EUREKA_MATERIALS_SUBSTANCE_INQUIRY,
    AccountDataAnalysisEventKey.EUREKA_MATERIALS_STRUCTURE_SEARCH,
    AccountDataAnalysisEventKey.EUREKA_MATERIALS_MATERIAL_SYNTHESIS_PROCESS,
    AccountDataAnalysisEventKey.EUREKA_MATERIALS_MATERIAL_PROFILE_SEARCH,
    AccountDataAnalysisEventKey.EUREKA_MATERIALS_COMPOSITION_SEARCH,
    AccountDataAnalysisEventKey.EUREKA_MATERIALS_SMART_SEARCH_PATENT,
    AccountDataAnalysisEventKey.EUREKA_MATERIALS_SMART_SEARCH_LITERATURE
];
export const defaultChartOrTableHeight = {
    defaultChartHeight: '450px',
    defaultTableHeight: '550px',
    defaultChartHeightWithTopBox: '435px',
    defaultTableHeightWithTopBox: '435px',
};
export var ACCOUNT_DATA_ANALYSIS_TABS;
(function (ACCOUNT_DATA_ANALYSIS_TABS) {
    ACCOUNT_DATA_ANALYSIS_TABS["Eureka"] = "eureka";
})(ACCOUNT_DATA_ANALYSIS_TABS || (ACCOUNT_DATA_ANALYSIS_TABS = {}));
export function makeAccountDataAnalysisQueries(tenant_user_id) {
    return {
        tenant_user_id,
        begin_at: getDateFormatYMD((new Date(new Date().setDate(new Date().getDate() - 30))), 'cn'),
        end_at: getDateFormatYMD((new Date(new Date().getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]), 'cn'),
        granularity: GRANULARITY.WEEK,
        identifier: '',
        package_ids: [],
    };
}
export function getAccountDataAnalysisParams(queries) {
    return {
        tenant_user_id: queries.tenant_user_id,
        begin_at: Date.parse(queries.begin_at),
        end_at: Date.parse(queries.end_at),
        granularity: queries.granularity,
    };
}
export function getAccountDataAnalysisSummaryParams(queries) {
    return {
        tenant_user_id: queries.tenant_user_id,
        begin_at: Date.parse(queries.begin_at),
        end_at: Date.parse(queries.end_at),
        granularity: queries.granularity,
        identifier: queries.identifier,
        package_ids: queries.package_ids,
    };
}
function makeEurekaActiveUserTableRows(data) {
    return _.map(data, item => {
        return {
            user_info: {
                name: item.name,
                email: item.email,
                mobile_number: item.mobile_number,
                user_id: item.user_id,
            },
            user_id: item.user_id,
            job_title: item.job_title,
            permissions: item.permissions,
            join_date: getDateFormatYMD(item.join_date),
            created_date: getDateFormatYMD(item.created_date),
            login_cnt: toThousands(item.eureka_login_cnt || 0),
            login_all_cnt: toThousands(item.eureka_login_all_cnt || 0),
            eureka_login_days: toThousands(item.eureka_login_days || 0),
            technical_qa: toThousands(item.technical_qa_pv || 0),
            find_solution: toThousands(item.find_solution_pv || 0),
            quick_research: toThousands(item.quick_research_pv || 0),
            evaluate_feasibility: toThousands(item.evaluate_feasibility_pv || 0),
            feasibility_analysis_assistant: toThousands(item.feasibility_analysis_assistant_pv || 0),
            homepage_search: toThousands(item.homepage_search_pv || 0),
            rd_patent_search: toThousands(item.rd_patent_search_pv || 0),
            rd_paper_search: toThousands(item.rd_paper_search_pv || 0),
            rd_novelty_check: toThousands(item.rd_novelty_check_pv || 0),
            rd_ai_disclosure: toThousands(item.rd_ai_disclosure_pv || 0),
            novelty_search: toThousands(item.novelty_search_pv || 0),
            specification_cn: toThousands(item.ip_specification_cn_pv || 0),
            specification_us: toThousands(item.ip_specification_us_pv || 0),
            specification_eu: toThousands(item.ip_specification_eu_pv || 0),
            invention_disclosure: toThousands(item.invention_disclosure_pv || 0),
            patent_translation: toThousands(item.patent_translation_pv || 0),
            design_fto_search: toThousands(item.design_fto_search_pv || 0),
            material_performance_analysis: toThousands(item.material_performance_analysis_pv || 0),
            alloy_composition_analysis: toThousands(item.alloy_composition_analysis_pv || 0),
            multi_d_search: toThousands(item.multi_d_search_pv || 0),
            substance_inquiry: toThousands(item.substance_inquiry_pv || 0),
            structure_search: toThousands(item.structure_search_pv || 0),
            material_synthesis_process: toThousands(item.material_synthesis_process_pv || 0),
            material_profile_search: toThousands(item.material_profile_search_pv || 0),
            composition_search: toThousands(item.composition_search_pv || 0),
            ms_patent_search: toThousands(item.ms_patent_search_pv || 0),
            ms_paper_search: toThousands(item.ms_paper_search_pv || 0),
            patent_view: toThousands(item.patent_view_pv || 0),
            paper_view: toThousands(item.paper_view_pv || 0),
        };
    });
}
let AccountDataAnalysisService = class AccountDataAnalysisService extends BaseDataAnalysisChartService {
    constructor(i18n, globalViewService, accountDataAnalysisApi) {
        super();
        this.i18n = i18n;
        this.globalViewService = globalViewService;
        this.accountDataAnalysisApi = accountDataAnalysisApi;
        this.dashBoard = [];
        this.dashBoardLoading = false;
    }
    toAccountDataAnalysisOptions(params) {
        return {
            name: E_ROUTE_ACCOUNT.ACCOUNT_DATA_ANALYSIS,
            params: {
                ...params,
                tab: ACCOUNT_DATA_ANALYSIS_TABS.Eureka,
            },
        };
    }
    getEurekaDashboard(data) {
        return [
            {
                title: this.i18n.t('backoffice.account.dataAnalysis.eurekaTotalCount'),
                key: AccountDataAnalysisUserSummaryKey.LOGIN_COUNT,
                count: toThousands(data.login_count || 0),
            },
            {
                title: this.i18n.t('backoffice.account.dataAnalysis.eurekaTotalDays'),
                key: AccountDataAnalysisUserSummaryKey.LOGIN_DAYS,
                count: toThousands(data.login_days || 0),
            }
        ];
    }
    getDashBoardData(params) {
        this.dashBoardLoading = true;
        this.accountDataAnalysisApi.postAccountDataAnalysisUserSummary(params).then(data => {
            // 若有更多的dashboard类型，再进行拓展
            if (params.report_name === AccountDataAnalysisKeyType.EUREKA_USER_COMPREHENSIVE) {
                this.dashBoard = this.getEurekaDashboard(data);
            }
        }).finally(() => {
            this.dashBoardLoading = false;
        });
    }
    getPvDaysStatistics({ total_count = 0, total_days = 0, key }) {
        const average = total_days !== 0 ? parseFloat((total_count / total_days).toFixed(2)) : 0;
        return [
            {
                label: this.i18n.t(`backoffice.company.dataAnalysis.${key}Pv`),
                tip: this.i18n.t(`backoffice.company.dataAnalysis.${key}PvTip`) === `backoffice.company.dataAnalysis.${key}PvTip` ? '' : this.i18n.t(`backoffice.company.dataAnalysis.${key}PvTip`),
                value: toThousands(total_count),
            },
            {
                label: this.i18n.t(`backoffice.company.dataAnalysis.${key}Days`),
                tip: this.i18n.t(`backoffice.company.dataAnalysis.${key}DaysTip`) === `backoffice.company.dataAnalysis.${key}DaysTip` ? '' : this.i18n.t(`backoffice.company.dataAnalysis.${key}DaysTip`),
                value: toThousands(total_days),
            },
            {
                label: this.i18n.t(`backoffice.company.dataAnalysis.${key}Avg`),
                tip: this.i18n.t(`backoffice.company.dataAnalysis.${key}AvgTip`) === `backoffice.company.dataAnalysis.${key}AvgTip` ? '' : this.i18n.t(`backoffice.company.dataAnalysis.${key}AvgTip`),
                value: toThousands(average),
            }
        ];
    }
    /**
     *  table columns
     */
    getPvDaysTableColumns(labels) {
        return _.compact([
            { label: labels.date, field: 'event_date', width: '120px' },
            labels.total_days && { label: labels.total_days, field: AccountDataAnalysisDataSet.TOTAL_DAYS },
            labels.total_count && { label: labels.total_count, field: AccountDataAnalysisDataSet.TOTAL_COUNT }
        ]);
    }
    /**
     * user detail table columns
     */
    getUserDetailPvTableColumns(labels) {
        return [
            { label: labels.date, field: 'event_date', width: '120px' },
            { label: labels.user, field: 'user' },
            { label: labels.total_count, field: AccountDataAnalysisDataSet.TOTAL_COUNT }
        ];
    }
    // User Login table columns
    getUserTableColumns(product) {
        const createdDateColumn = { label: this.i18n.t('backoffice.company.dataAnalysis.createdDate'), field: AccountDataAnalysisEventKey.CREATED_DATE, width: '120px', checked: true, sortable: true };
        const loginCountColumn = { label: this.i18n.t(`backoffice.company.dataAnalysis.${product}LoginCount`), field: AccountDataAnalysisEventKey.LOGIN_CNT, width: '240px', checked: true, sortable: true };
        const loginTotalCountColumn = { label: this.i18n.t(`backoffice.company.dataAnalysis.${product}LoginTotalCount`), field: AccountDataAnalysisEventKey.LOGIN_ALL_CNT, width: '240px', checked: true, sortable: true };
        const loginDaysColumn = { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaLoginDays'), field: AccountDataAnalysisEventKey.EUREKA_LOGIN_DAYS, width: '240px', checked: true, sortable: true };
        return [
            { label: this.i18n.t('backoffice.company.dataAnalysis.joinDate'), field: AccountDataAnalysisEventKey.JOIN_DATE, width: '120px', fixed: GoodTableColumnFixedType.LEFT, checked: true, sortable: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.User'), field: AccountDataAnalysisEventKey.USER_INFO, width: '240px', fixed: GoodTableColumnFixedType.LEFT, checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.jobTtitle'), field: AccountDataAnalysisEventKey.JOB_TITLE, width: '200px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.permission'), field: AccountDataAnalysisEventKey.PERMISSIONS, width: '240px', checked: true },
            ...(product === AccountDataAnalysisEventKey.EUREKA_LOGIN_CNT ? [loginDaysColumn] : []),
            ...(product !== AccountDataAnalysisEventKey.USER_INFO ? [loginCountColumn, loginTotalCountColumn] : []),
            ...(product !== AccountDataAnalysisEventKey.USER_INFO ? [createdDateColumn] : []),
            { label: this.i18n.t('backoffice.account.userId'), field: AccountDataAnalysisEventKey.USER_ID, width: '300px', checked: true }
        ];
    }
    getEurekaActiveUserTableColumns() {
        return [
            // { label: this.i18n.t('backoffice.company.dataAnalysis.User'), field: AccountDataAnalysisEventKey.USER_INFO, width: '280px', fixed: GoodTableColumnFixedType.LEFT, checked: true },
            { label: this.i18n.t('backoffice.account.userId'), field: AccountDataAnalysisEventKey.USER_ID, width: '300px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.jobTtitle'), field: AccountDataAnalysisEventKey.JOB_TITLE, width: '200px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.permission'), field: AccountDataAnalysisEventKey.PERMISSIONS, width: '240px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.joinDate'), field: AccountDataAnalysisEventKey.JOIN_DATE, width: '120px', checked: true, sortable: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.createdDate'), field: AccountDataAnalysisEventKey.CREATED_DATE, width: '120px', checked: true, sortable: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaLoginDays'), field: AccountDataAnalysisEventKey.EUREKA_LOGIN_DAYS, width: '240px', checked: true, sortable: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaLoginCount'), field: AccountDataAnalysisEventKey.LOGIN_CNT, width: '240px', checked: true, sortable: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaLoginTotalCount'), field: AccountDataAnalysisEventKey.LOGIN_ALL_CNT, width: '240px', checked: true, sortable: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdTechnicalQa'), field: AccountDataAnalysisEventKey.EUREKA_RD_TECHNICAL_QA, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdFindSolution'), field: AccountDataAnalysisEventKey.EUREKA_RD_FIND_SOLUTION, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdQuickResearch'), field: AccountDataAnalysisEventKey.EUREKA_RD_QUICK_RESEARCH, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdEvaluateFeasibility'), field: AccountDataAnalysisEventKey.EUREKA_RD_EVALUATE_FEASIBILITY, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdFeasibilityAnalysisAssistant'), field: AccountDataAnalysisEventKey.EUREKA_RD_FEASIBILITY_ANALYSIS_ASSISTANT, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdHomepageSearch'), field: AccountDataAnalysisEventKey.EUREKA_RD_HOMEPAGE_SEARCH, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdPatentSearch'), field: AccountDataAnalysisEventKey.EUREKA_RD_PATENT_SEARCH, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdLiteratureSearch'), field: AccountDataAnalysisEventKey.EUREKA_RD_LITERATURE_SEARCH, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdIdeaValidation'), field: AccountDataAnalysisEventKey.EUREKA_RD_IDEA_VALIDATION, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdInventionDisclosure'), field: AccountDataAnalysisEventKey.EUREKA_RD_INVENTION_DISCLOSURE, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPNoveltySearch'), field: AccountDataAnalysisEventKey.EUREKA_IP_NOVELTY_SEARCH, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPPatentDraftingCN'), field: AccountDataAnalysisEventKey.EUREKA_IP_PATENT_DRAFTING_CN, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPPatentDraftingUS'), field: AccountDataAnalysisEventKey.EUREKA_IP_PATENT_DRAFTING_US, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPPatentDraftingEU'), field: AccountDataAnalysisEventKey.EUREKA_IP_PATENT_DRAFTING_EU, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPInventionDisclosure'), field: AccountDataAnalysisEventKey.EUREKA_IP_INVENTION_DISCLOSURE, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPPatentTranslation'), field: AccountDataAnalysisEventKey.EUREKA_IP_PATENT_TRANSLATION, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPDesignFTOSearch'), field: AccountDataAnalysisEventKey.EUREKA_IP_DESIGN_FTO_SEARCH, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialPerformanceAnalysis'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_MATERIAL_PERFORMANCE_ANALYSIS, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialAlloyCompositionAnalysis'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_ALLOY_COMPOSITION_ANALYSIS, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialMultiDSearch'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_MULTI_D_SEARCH, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialSubstanceInquiry'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_SUBSTANCE_INQUIRY, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialStructureSearch'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_STRUCTURE_SEARCH, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialSynthesisProcess'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_MATERIAL_SYNTHESIS_PROCESS, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialProfileSearch'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_MATERIAL_PROFILE_SEARCH, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialCompositionSearch'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_COMPOSITION_SEARCH, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialSmartSearchPatent'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_SMART_SEARCH_PATENT, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialSmartSearchLiterature'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_SMART_SEARCH_LITERATURE, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaPatentView'), field: AccountDataAnalysisEventKey.EUREKA_PATENT_VIEW, width: '140px', checked: true },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaPaperView'), field: AccountDataAnalysisEventKey.EUREKA_PAPER_VIEW, width: '140px', checked: true }
        ];
    }
    getEurekaRdTableColumns() {
        return [
            { label: this.i18n.t('backoffice.company.dataAnalysis.date'), field: AccountDataAnalysisEventKey.DATE, width: '120px', fixed: GoodTableColumnFixedType.LEFT },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdTechnicalQa'), field: AccountDataAnalysisEventKey.EUREKA_RD_TECHNICAL_QA, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdFindSolution'), field: AccountDataAnalysisEventKey.EUREKA_RD_FIND_SOLUTION, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdQuickResearch'), field: AccountDataAnalysisEventKey.EUREKA_RD_QUICK_RESEARCH, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdEvaluateFeasibility'), field: AccountDataAnalysisEventKey.EUREKA_RD_EVALUATE_FEASIBILITY, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdFeasibilityAnalysisAssistant'), field: AccountDataAnalysisEventKey.EUREKA_RD_FEASIBILITY_ANALYSIS_ASSISTANT, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdHomepageSearch'), field: AccountDataAnalysisEventKey.EUREKA_RD_HOMEPAGE_SEARCH, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdPatentSearch'), field: AccountDataAnalysisEventKey.EUREKA_RD_PATENT_SEARCH, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdLiteratureSearch'), field: AccountDataAnalysisEventKey.EUREKA_RD_LITERATURE_SEARCH, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdIdeaValidation'), field: AccountDataAnalysisEventKey.EUREKA_RD_IDEA_VALIDATION, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaRdInventionDisclosure'), field: AccountDataAnalysisEventKey.EUREKA_RD_INVENTION_DISCLOSURE, width: '140px' }
        ];
    }
    getEurekaIPTableColumns() {
        return [
            { label: this.i18n.t('backoffice.company.dataAnalysis.date'), field: AccountDataAnalysisEventKey.DATE, width: '120px', fixed: GoodTableColumnFixedType.LEFT },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPNoveltySearch'), field: AccountDataAnalysisEventKey.EUREKA_IP_NOVELTY_SEARCH },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPPatentDraftingCN'), field: AccountDataAnalysisEventKey.EUREKA_IP_PATENT_DRAFTING_CN },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPPatentDraftingUS'), field: AccountDataAnalysisEventKey.EUREKA_IP_PATENT_DRAFTING_US },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPPatentDraftingEU'), field: AccountDataAnalysisEventKey.EUREKA_IP_PATENT_DRAFTING_EU },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPInventionDisclosure'), field: AccountDataAnalysisEventKey.EUREKA_IP_INVENTION_DISCLOSURE },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPPatentTranslation'), field: AccountDataAnalysisEventKey.EUREKA_IP_PATENT_TRANSLATION },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaIPDesignFTOSearch'), field: AccountDataAnalysisEventKey.EUREKA_IP_DESIGN_FTO_SEARCH }
        ];
    }
    getEurekaMaterialsTableColumns() {
        return [
            { label: this.i18n.t('backoffice.company.dataAnalysis.date'), field: AccountDataAnalysisEventKey.DATE, width: '120px', fixed: GoodTableColumnFixedType.LEFT },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialPerformanceAnalysis'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_MATERIAL_PERFORMANCE_ANALYSIS, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialAlloyCompositionAnalysis'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_ALLOY_COMPOSITION_ANALYSIS, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialMultiDSearch'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_MULTI_D_SEARCH, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialSubstanceInquiry'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_SUBSTANCE_INQUIRY, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialStructureSearch'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_STRUCTURE_SEARCH, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialSynthesisProcess'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_MATERIAL_SYNTHESIS_PROCESS, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialProfileSearch'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_MATERIAL_PROFILE_SEARCH, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialCompositionSearch'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_COMPOSITION_SEARCH, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialSmartSearchPatent'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_SMART_SEARCH_PATENT, width: '140px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.eurekaMaterialSmartSearchLiterature'), field: AccountDataAnalysisEventKey.EUREKA_MATERIALS_SMART_SEARCH_LITERATURE, width: '140px' }
        ];
    }
    /**
     * 通用pv days bar chart option
     */
    getPvDaysBarChartOption(params) {
        const { data, total_count, total_days, key } = params;
        const options = getBarOptions();
        const sortedData = _.sortBy(data, 'event_date').reverse();
        const formattedSource = sortedData.map(item => [
            this.getDateFormat(item[key].event_date),
            item[key].total_count,
            item[key].total_days
        ]);
        const baseSource = [['product', total_count, total_days], ...formattedSource];
        options.dataset = { source: baseSource };
        options.series = [this.getBarOptionSeries(1), this.getBarOptionSeries(2)];
        if (total_days && !total_count) {
            options.dataset = { source: [['product', total_days], ...formattedSource.map(item => [item[0], item[2]])] };
            options.series = [this.getBarOptionSeries(1)];
        }
        return this.getChartOptionWithZoom(options, data.length);
    }
    getLineOptionDatesAndSeries(params) {
        const { data, sortList, dataSet, columns } = params;
        // 图表从旧到新排序
        const details = _.sortBy(data.data_detail, data => data[sortList[0]].event_date);
        // 生成 key:value as []
        const keysMap = _.zipObject(sortList, _.times(sortList.length, () => []));
        // 生成 日期
        const dates = [];
        _.forEach(details, item => {
            _.forEach(sortList, key => {
                keysMap[key].push(item[key] ? item[key][dataSet] : 0);
            });
            dates.push(this.getDateFormat(item[sortList[0]].event_date));
        });
        const series = this.generateLineSeries(keysMap, columns);
        return {
            dates,
            series,
        };
    }
    getAccountLineChartOption(params) {
        const { dates, series } = this.getLineOptionDatesAndSeries(params);
        return super.getLineChartOption(dates, series);
    }
    /**
     * 处理堆叠图返回数据
     */
    getStackBarSeries(params) {
        const { data, dataSet, sortList, columns } = params;
        // 图表从旧到新排序
        const details = _.sortBy(data.data_detail, data => data[sortList[0]].event_date);
        if (details.length === 0)
            return;
        // 生成 key:value as []
        const keysMap = _.zipObject(sortList, _.times(sortList.length, () => []));
        // 生成 日期
        const dates = [];
        // 遍历list 将list[index]中的key的pv uv 塞入 keysMap
        _.forEach(details, item => {
            _.forEach(sortList, key => {
                keysMap[key].push(item[key] ? item[key][dataSet] : 0);
            });
            dates.push(this.getDateFormat(item[sortList[0]].event_date));
        });
        // 计算total
        const total = this.calculateStackTotal(keysMap);
        // 用 keysMap 生成 series
        const series = this.generateStackBarSeries(keysMap, columns, total, (key) => key === _.last(columns).field);
        return {
            series,
            dates,
        };
    }
    getStackBarCharOption(params) {
        const { data, dataSet, sortList, colorList, columns } = params;
        const seriesParams = {
            data: data,
            dataSet: dataSet,
            sortList: sortList,
            columns: columns,
        };
        const { series, dates } = this.getStackBarSeries(seriesParams);
        const options = this.getBarChartOption(dates, series, colorList);
        return this.getChartOptionWithZoom(options, dates.length);
    }
    getSortFunctions(reportName) {
        const sortFunction = sortFunctionMap[reportName];
        return sortFunction ? [sortFunction] : [];
    }
    getDetailTableDefaultParams(params, reportName) {
        const { sEcho, iDisplayLength, iDisplayStart, ...otherParams } = params;
        return {
            sEcho: sEcho || 1,
            iDisplayLength: iDisplayLength || 50,
            iDisplayStart: iDisplayStart || 0,
            report_name: reportName,
            order_bys: this.getSortFunctions(reportName),
            ...otherParams,
        };
    }
    getPvDaysTableRows(data, key) {
        return _.map(data, item => {
            return {
                event_date: this.getDateFormat(item[`${key}`].event_date),
                total_count: toThousands(item[`${key}`].total_count),
                total_days: toThousands(item[`${key}`].total_days),
            };
        });
    }
    getPvDetailTableRows(data) {
        return _.map(data, item => {
            return {
                event_date: getDateFormatYMD(item.event_date),
                total_count: toThousands(item.total_count),
                user: item,
            };
        });
    }
    getUserTableRows(data, product) {
        const loginCnt = `${product}_login_cnt`;
        const loginAllCnt = `${product}_login_all_cnt`;
        return _.map(data, item => {
            return {
                join_date: getDateFormatYMD(item.join_date),
                user_info: {
                    name: item.name,
                    email: item.email,
                    mobile_number: item.mobile_number,
                    user_id: item.user_id,
                },
                job_title: item.job_title,
                user_id: item.user_id,
                permissions: item.permissions,
                eureka_login_days: toThousands(item.eureka_login_days),
                login_cnt: toThousands(item[loginCnt]),
                login_all_cnt: toThousands(item[loginAllCnt]),
                created_date: getDateFormatYMD(item.created_date),
            };
        });
    }
    getEurekaActiveUserTableRows(data) {
        return makeEurekaActiveUserTableRows(data);
    }
    // 字段设置
    getFiledSettingColumns(key, baseColumns) {
        return this.globalViewService.getGlobalViewFields(key).then((res) => {
            if (res.config_value) {
                const settingColumns = JSON.parse(res.config_value);
                // 如果设置的列数和默认列数一样，就按照设置的顺序排序
                if (settingColumns.length === baseColumns.length) {
                    baseColumns = _.sortBy(baseColumns, column => _.indexOf(settingColumns.map(col => col.field), column.field));
                }
                // 同步checked状态
                _.forEach(baseColumns, i => {
                    const column = _.find(settingColumns, col => col.field === i.field);
                    i.checked = column ? column.checked || false : true;
                });
                return baseColumns;
            }
            return baseColumns;
        });
    }
};
AccountDataAnalysisService = __decorate([
    injectable(),
    __param(0, inject(Symbols.I18n)),
    __param(1, inject(Symbols.GlobalViewService)),
    __param(2, inject(Symbols.AccountDataAnalysisApi)),
    __metadata("design:paramtypes", [Object, Object, Object])
], AccountDataAnalysisService);
export { AccountDataAnalysisService };
export class AccountDataAnalysisOptions extends BaseDataAnalysisOptions {
    constructor() {
        super(...arguments);
        /**
         * 表格字段
         */
        this.tableFields = [];
        // 统计
        this.statistics = [];
        /**
         * 视图类型
         */
        this.viewType = AccountDataAnalysisViewType.Chart;
    }
}
